import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Grow a fixed-height grid to the bottom of the visible content area.
 *
 * The main list grids size themselves against the viewport, so zooming the
 * browser out (which enlarges the viewport in CSS pixels) reveals more rows.
 * Grids given a fixed pixel height — dashboard report grids, detail-page
 * sub-grids — stay the same number of rows no matter the zoom level. This
 * hook measures the space between the grid's top edge and the bottom of the
 * `.sky-content` scroll viewport and returns `max(minHeight, available)`, so
 * a configured height acts as a floor while extra vertical space (zoom-out,
 * tall monitors) is put to use.
 *
 * Attach `ref` to the element that should receive the returned `height`.
 * Re-measures on window resize (browser zoom fires resize) and whenever the
 * scroll container's content resizes (async KPI cards/charts above the grid).
 */
export function useFillGridHeight(minHeight: number, enabled = true): {
  ref: React.MutableRefObject<HTMLDivElement | null>;
  height: number;
} {
  const ref = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(minHeight);

  const measure = useCallback(() => {
    const element = ref.current;
    if (!element) return;
    // Skip hidden elements (e.g. grids in an unselected dashboard tab panel):
    // their rects are zeroed and would mis-measure. They re-measure when
    // revealed, via the ResizeObserver on the element itself.
    if (element.offsetParent === null) return;
    const scroller = element.closest<HTMLElement>('.sky-content');
    if (!scroller) {
      setHeight(minHeight);
      return;
    }
    // Distance from the scroll container's top edge to the grid, independent
    // of the current scroll position.
    const offsetTop = element.getBoundingClientRect().top
      - scroller.getBoundingClientRect().top
      + scroller.scrollTop;
    const bottomPadding = Number.parseFloat(getComputedStyle(scroller).paddingBottom) || 0;
    const available = Math.floor(scroller.clientHeight - offsetTop - bottomPadding);
    setHeight(Math.max(minHeight, available));
  }, [minHeight]);

  useLayoutEffect(() => {
    if (!enabled) return undefined;
    measure();
    const element = ref.current;
    const scroller = element?.closest<HTMLElement>('.sky-content');
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null;
    if (observer && element && scroller) {
      // The element itself resizes from 0 when its tab panel is revealed.
      observer.observe(element);
      observer.observe(scroller);
      // The scroller's direct children grow as async content (stat cards,
      // charts) loads above the grid; observing them catches those shifts.
      for (const child of Array.from(scroller.children)) observer.observe(child);
    }
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure, enabled]);

  return { ref, height: enabled ? height : minHeight };
}
